import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Plus, Search, Upload, User, FileImage, X, Pencil, Trash2, Users2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import CustomerEditDialog from './CustomerEditDialog';
import CustomerAssignDialog from './CustomerAssignDialog';
import ImagePreview from '../common/ImagePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fileToDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const DOC_FIELDS = [
  { key: 'photo_url', label: 'Customer Photo' },
  { key: 'cnic_front_url', label: 'CNIC Front' },
  { key: 'cnic_back_url', label: 'CNIC Back' },
  { key: 'signature_url', label: 'Signature' },
  { key: 'thumb_impression_url', label: 'Thumb Impression' },
];

function ImageUploader({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(`${label} must be under 2MB`); return; }
    const url = await fileToDataURL(file);
    onChange(url);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-slate-300 text-xs">{label}</Label>
      <div className="border border-dashed border-slate-700 rounded-sm p-2 bg-slate-800/40 min-h-[90px] flex items-center justify-center relative">
        {value ? (
          <>
            <img src={value} alt={label} className="max-h-24 object-contain" />
            <button type="button" onClick={() => onChange('')}
              className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-500 rounded-sm text-white">
              <X className="h-3 w-3" />
            </button>
          </>
        ) : (
          <button type="button" onClick={() => ref.current?.click()}
            data-testid={testId}
            className="flex flex-col items-center text-slate-500 hover:text-amber-500 text-xs">
            <Upload className="h-4 w-4 mb-1" />
            Upload
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </div>
    </div>
  );
}

const emptyForm = {
  name: '', relation_type: 'Father', father_name: '', husband_name: '', cnic: '', mobile: '', address: '',
  tracking_number: '',
  dob: '', cnic_expiry_date: '', department: '', occupation: '', employment_type: '', income: '',
  sales_executive_name: '', server_name: '',
  photo_url: '', cnic_front_url: '', cnic_back_url: '', signature_url: '', thumb_impression_url: '',
  guarantor_name: '', guarantor_relation_type: 'Father', guarantor_father_name: '', guarantor_husband_name: '',
  guarantor_profession: '', guarantor_cnic: '', guarantor_mobile: '', guarantor_address: '', guarantor_relation: '',
};

// Calculate age from an ISO YYYY-MM-DD string. Always live (recomputed every render).
export const calcAge = (dob) => {
  if (!dob) return '';
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? `${age}` : '';
};

export default function CustomerManagement() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editing, setEditing] = useState({ open: false, customer: null });
  const [assigning, setAssigning] = useState({ open: false, customer: null });
  const [preview, setPreview] = useState({ open: false, src: null, title: '' });
  const [branches, setBranches] = useState([]);
  const [assignBranchId, setAssignBranchId] = useState('');

  const canEdit = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const canDelete = ['Super Admin', 'Admin'].includes(user?.role);
  const canAssign = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const isHQ = ['Super Admin', 'Admin'].includes(user?.role);

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/customers/delete/${c.id}`, { withCredentials: true });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  useEffect(() => {
    fetchCustomers();
    axios.get(`${API_URL}/api/branches`, { withCredentials: true })
      .then(({ data }) => setBranches(data || []))
      .catch(() => setBranches([]));
  }, []);

  const fetchCustomers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/customers`, { withCredentials: true });
      setCustomers(data);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchCustomers(); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/customers/search?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setCustomers(data);
    } catch { toast.error('Search failed'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        income: formData.income ? Number(formData.income) : null,
        branch_id: isHQ && assignBranchId ? assignBranchId : undefined,
      };
      await axios.post(`${API_URL}/api/customers`, payload, { withCredentials: true });
      toast.success('Customer created with documents');
      setDialogOpen(false);
      setFormData(emptyForm);
      setAssignBranchId('');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create');
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" data-testid="customers-title">Customers</h1>
          <p className="text-slate-400">Full KYC — photo, CNIC, signature, thumb impression</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-customer-button">
              <Plus className="h-4 w-4 mr-2" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Customer</DialogTitle>
              <DialogDescription className="text-slate-400">Capture full KYC in a single form.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Tabs defaultValue="personal">
                <TabsList className="bg-slate-800 border border-slate-700">
                  <TabsTrigger value="personal" data-testid="tab-personal" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Personal</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Documents</TabsTrigger>
                  <TabsTrigger value="guarantor" data-testid="tab-guarantor" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">Guarantor</TabsTrigger>
                </TabsList>

                <TabsContent value="personal" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-slate-300">Name *</Label>
                      <Input required data-testid="customer-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Relation Type *</Label>
                      <select required data-testid="customer-relation-type"
                        value={formData.relation_type || 'Father'}
                        onChange={(e) => setFormData({ ...formData, relation_type: e.target.value })}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                        <option value="Father">Father</option>
                        <option value="Husband">Husband</option>
                      </select>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-slate-300">{formData.relation_type === 'Husband' ? 'Husband Name' : 'Father Name'}</Label>
                      <Input
                        data-testid="customer-relation-name"
                        value={formData.relation_type === 'Husband' ? formData.husband_name : formData.father_name}
                        onChange={(e) => {
                          if (formData.relation_type === 'Husband') {
                            setFormData({ ...formData, husband_name: e.target.value, father_name: '' });
                          } else {
                            setFormData({ ...formData, father_name: e.target.value, husband_name: '' });
                          }
                        }}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                    <div className="space-y-2"><Label className="text-slate-300">Department</Label>
                      <Input data-testid="customer-department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Date of Birth</Label>
                      <Input type="date" data-testid="customer-dob" value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Age (auto)</Label>
                      <Input readOnly data-testid="customer-age" value={calcAge(formData.dob)} placeholder="—" className="bg-slate-800/60 border-slate-700 text-amber-500" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">CNIC *</Label>
                      <Input required data-testid="customer-cnic-input" placeholder="12345-1234567-1" value={formData.cnic} onChange={(e) => setFormData({ ...formData, cnic: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">CNIC Expiry Date</Label>
                      <Input type="date" data-testid="customer-cnic-expiry"
                        value={formData.cnic_expiry_date}
                        onChange={(e) => setFormData({ ...formData, cnic_expiry_date: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                      <p className="text-[10px] text-slate-500">You'll be alerted 30 days before it expires.</p>
                    </div>
                    <div className="space-y-2"><Label className="text-slate-300">Mobile *</Label>
                      <Input required data-testid="customer-mobile-input" placeholder="03001234567" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Tracking Number</Label>
                      <Input
                        data-testid="customer-tracking-number"
                        placeholder="Leave blank to auto-generate"
                        value={formData.tracking_number}
                        onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                      <p className="text-[10px] text-slate-500">Optional — unique per customer. Auto-assigned if left blank.</p>
                    </div>
                    <div className="space-y-2 col-span-2"><Label className="text-slate-300">Address *</Label>
                      <Textarea required data-testid="customer-address-input" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-slate-800 border-slate-700 text-white" rows={2} /></div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-slate-300">Sales Executive By</Label>
                      <Input data-testid="customer-sales-executive"
                        placeholder="Custom name shown on the Agreement / Print"
                        value={formData.sales_executive_name}
                        onChange={(e) => setFormData({ ...formData, sales_executive_name: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                      <p className="text-xs text-slate-500">Prints on Agreement, Preview, PDF and Print. If left blank, the creating user's name is used.</p>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label className="text-slate-300">Server Name</Label>
                      <Input data-testid="customer-server-name"
                        placeholder="Recovery / server person assigned to this customer"
                        value={formData.server_name}
                        onChange={(e) => setFormData({ ...formData, server_name: e.target.value })}
                        className="bg-slate-800 border-slate-700 text-white" />
                      <p className="text-xs text-slate-500">Only visible inside the Customer Profile — not printed on agreements or listings.</p>
                    </div>
                    <div className="space-y-2"><Label className="text-slate-300">Occupation</Label>
                      <Input value={formData.occupation} onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Employment Type</Label>
                      <select
                        data-testid="customer-employment-type"
                        value={formData.employment_type}
                        onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                        <option value="">Select…</option>
                        <option value="Government">Government (salaried)</option>
                        <option value="Private">Private (salaried)</option>
                        <option value="Business">Business / Self-employed</option>
                        <option value="Other">Other</option>
                      </select>
                      <p className="text-xs text-slate-500">Government / Private will print a Head of Office Verification card on the agreement PDF.</p>
                    </div>
                    <div className="space-y-2"><Label className="text-slate-300">Monthly Income (PKR)</Label>
                      <Input type="number" value={formData.income} onChange={(e) => setFormData({ ...formData, income: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    {isHQ && (
                      <div className="space-y-2 col-span-2 border-t border-slate-800 pt-3">
                        <Label className="text-slate-300">Assign to Branch (HQ only — optional at creation)</Label>
                        <select
                          value={assignBranchId}
                          onChange={(e) => setAssignBranchId(e.target.value)}
                          data-testid="customer-branch-select"
                          className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                          <option value="">Leave with HQ (assign later)</option>
                          {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                        </select>
                        <p className="text-xs text-slate-500">Leave blank if the Branch Manager will assign this customer later.</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {DOC_FIELDS.map((f) => (
                      <ImageUploader
                        key={f.key}
                        label={f.label}
                        value={formData[f.key]}
                        onChange={(v) => setFormData({ ...formData, [f.key]: v })}
                        testId={`upload-${f.key}`}
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="guarantor" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-slate-300">Guarantor Name</Label>
                      <Input data-testid="guarantor-name-input" value={formData.guarantor_name} onChange={(e) => setFormData({ ...formData, guarantor_name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Guarantor CNIC</Label>
                      <Input value={formData.guarantor_cnic} onChange={(e) => setFormData({ ...formData, guarantor_cnic: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Relation Type</Label>
                      <select
                        data-testid="guarantor-relation-type"
                        value={formData.guarantor_relation_type || 'Father'}
                        onChange={(e) => setFormData({ ...formData, guarantor_relation_type: e.target.value })}
                        className="w-full bg-slate-800 border border-slate-700 text-white rounded-sm h-9 px-3 text-sm">
                        <option value="Father">Father</option>
                        <option value="Husband">Husband</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">{formData.guarantor_relation_type === 'Husband' ? 'Husband Name' : 'Father Name'}</Label>
                      <Input
                        data-testid="guarantor-relation-name"
                        value={formData.guarantor_relation_type === 'Husband' ? formData.guarantor_husband_name : formData.guarantor_father_name}
                        onChange={(e) => {
                          if (formData.guarantor_relation_type === 'Husband') {
                            setFormData({ ...formData, guarantor_husband_name: e.target.value, guarantor_father_name: '' });
                          } else {
                            setFormData({ ...formData, guarantor_father_name: e.target.value, guarantor_husband_name: '' });
                          }
                        }}
                        className="bg-slate-800 border-slate-700 text-white" />
                    </div>
                    <div className="space-y-2"><Label className="text-slate-300">Profession</Label>
                      <Input data-testid="guarantor-profession" value={formData.guarantor_profession} onChange={(e) => setFormData({ ...formData, guarantor_profession: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Guarantor Mobile</Label>
                      <Input value={formData.guarantor_mobile} onChange={(e) => setFormData({ ...formData, guarantor_mobile: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2"><Label className="text-slate-300">Relationship</Label>
                      <Input placeholder="Father / Brother / Uncle" value={formData.guarantor_relation} onChange={(e) => setFormData({ ...formData, guarantor_relation: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
                    <div className="space-y-2 col-span-2"><Label className="text-slate-300">Guarantor Address</Label>
                      <Textarea value={formData.guarantor_address} onChange={(e) => setFormData({ ...formData, guarantor_address: e.target.value })} className="bg-slate-800 border-slate-700 text-white" rows={2} /></div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end space-x-2 border-t border-slate-800 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300">Cancel</Button>
                <Button type="submit" data-testid="submit-customer-button" className="bg-amber-500 hover:bg-amber-600">Create Customer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Search className="h-5 w-5 text-slate-400" />
            <Input data-testid="search-customers-input" placeholder="Search by name, CNIC, mobile, account number..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="bg-slate-800 border-slate-700 text-white" />
            <Button onClick={handleSearch} data-testid="search-button" className="bg-amber-500 hover:bg-amber-600">Search</Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8 text-slate-400">Loading...</div>
            : customers.length === 0 ? <div className="text-center py-8 text-slate-400" data-testid="no-customers-message">No customers found</div>
            : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400 w-14"></TableHead>
                    <TableHead className="text-slate-400">Customer ID</TableHead>
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Account #</TableHead>
                    <TableHead className="text-slate-400">CNIC</TableHead>
                    <TableHead className="text-slate-400">Mobile</TableHead>
                    <TableHead className="text-slate-400">Docs</TableHead>
                    <TableHead className="text-slate-400">Branch / Assignee</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((c) => {
                    const docCount = DOC_FIELDS.filter(f => c[f.key]).length;
                    const branchName = branches.find(b => b.id === c.branch_id)?.name || (c.branch_id ? 'Branch' : 'HQ');
                    return (
                      <TableRow key={c.id} className="border-slate-800 hover:bg-slate-800/50" data-testid="customer-row">
                        <TableCell>
                          {c.photo_url ? (
                            <button type="button"
                              onClick={() => setPreview({ open: true, src: c.photo_url, title: `${c.name} — Photo` })}
                              data-testid={`preview-photo-${c.id}`}
                              title="Click to preview"
                              className="block">
                              <img src={c.photo_url} alt={c.name} className="h-16 w-16 rounded-sm object-cover border border-slate-700 hover:border-amber-500 transition" />
                            </button>
                          ) : (
                            <div className="h-16 w-16 rounded-sm bg-slate-800 flex items-center justify-center text-slate-500"><User className="h-8 w-8" /></div>
                          )}
                        </TableCell>
                        <TableCell className="text-white font-medium text-xs">{c.customer_id}</TableCell>
                        <TableCell className="text-white">{c.name}</TableCell>
                        <TableCell className="text-slate-300 text-xs">{c.account_number}</TableCell>
                        <TableCell className="text-slate-300">{c.cnic}</TableCell>
                        <TableCell className="text-slate-300">{c.mobile}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs ${docCount === 5 ? 'bg-emerald-500/20 text-emerald-500' : docCount > 0 ? 'bg-amber-500/20 text-amber-500' : 'bg-slate-700/50 text-slate-500'}`}>
                              <FileImage className="h-3 w-3 mr-1" />{docCount}/5
                            </span>
                            {DOC_FIELDS.filter(f => c[f.key]).map((f) => (
                              <button key={f.key} type="button"
                                onClick={() => setPreview({ open: true, src: c[f.key], title: `${c.name} — ${f.label}`, downloadName: `${c.customer_id || c.name}_${f.label}.png` })}
                                data-testid={`preview-doc-${c.id}-${f.key}`}
                                title={`Preview ${f.label}`}
                                className="w-6 h-6 rounded-sm overflow-hidden border border-slate-700 hover:border-amber-500">
                                <img src={c[f.key]} alt={f.label} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-col">
                            <span className={c.branch_id ? 'text-emerald-500' : 'text-slate-500'}>{branchName}</span>
                            <span className={c.assigned_user_id ? 'text-cyan-400' : 'text-slate-500'}>
                              {c.assigned_user_id ? 'Assigned' : 'Unassigned'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-1">
                            {canAssign && (
                              <Button size="sm" variant="ghost"
                                onClick={() => setAssigning({ open: true, customer: c })}
                                data-testid={`assign-customer-${c.id}`}
                                className="text-cyan-400 hover:text-cyan-300 hover:bg-slate-800 h-8 w-8 p-0" title="Assign branch / user">
                                <Users2 className="h-4 w-4" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button size="sm" variant="ghost"
                                onClick={() => setEditing({ open: true, customer: c })}
                                data-testid={`edit-customer-${c.id}`}
                                className="text-blue-400 hover:text-blue-300 hover:bg-slate-800 h-8 w-8 p-0" title="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="ghost"
                                onClick={() => handleDelete(c)}
                                data-testid={`delete-customer-${c.id}`}
                                className="text-red-400 hover:text-red-300 hover:bg-slate-800 h-8 w-8 p-0" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <CustomerEditDialog
        open={editing.open}
        onOpenChange={(o) => setEditing({ ...editing, open: o })}
        customer={editing.customer}
        onSaved={fetchCustomers}
      />
      <CustomerAssignDialog
        open={assigning.open}
        onOpenChange={(o) => setAssigning({ ...assigning, open: o })}
        customer={assigning.customer}
        currentUser={user}
        branches={branches}
        onSaved={fetchCustomers}
      />
      <ImagePreview
        open={preview.open}
        onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}
        src={preview.src}
        title={preview.title}
        downloadName={preview.downloadName}
      />
    </div>
  );
}
